import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with automatic recovery from stale chunk hashes after
 * a new deploy. If a dynamic import fails (chunk not found), we reload the
 * page once so the browser picks up the fresh index.html + new hashes.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_KEY = "lovable:chunk-reload";
    try {
      const mod = await factory();
      if (typeof window !== "undefined") sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err: any) {
      const msg = String(err?.message || err);
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Loading chunk [\d]+ failed/i.test(msg) ||
        /Importing a module script failed/i.test(msg);

      if (isChunkError && typeof window !== "undefined") {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          // Return a never-resolving promise while the reload happens
          return new Promise(() => {}) as any;
        }
      }
      throw err;
    }
  });
}
